import {
  ActionButton,
  DataObjectGroupType,
  Form,
  FormAlerts,
  GetApiErrorMessage,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useAlertContext,
  useDataObjectGroupsWithDescriptionsQuery,
  useGroupDescriptionOptions,
  useMutationErrors,
  useTimeSeriesValidateOutputData,
  ValidateTimeSeriesSchema,
  ValidateTimeSeriesType,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import React, { FC } from 'react';
import translations from './TimeSeriesActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TimeSeriesOutputsTable } from './TimeSeriesOutputsTable';
import { useWatch } from 'react-hook-form';
import { OutputQuickSelect } from './OutputQuickSelect';

const { addTranslations, t, exists } = i18nNsFactory(
  'time-series-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface TimeSeriesValidationModalProps {
  onClose: () => void;
}

export const TimeSeriesValidationModal: React.FC<
  TimeSeriesValidationModalProps
> = ({ onClose }) => {
  const { mutateAsync } = useTimeSeriesValidateOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  return (
    <Form
      schema={ValidateTimeSeriesSchema}
      onSubmit={(data: ValidateTimeSeriesType) => {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({ data })
          .then(() => {
            onClose();
            setSuccess(t('alert.success.validated'));
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <TimeSeriesValidationModalInner onClose={onClose} />
    </Form>
  );
};

const TimeSeriesValidationModalInner: FC<{
  onClose: () => void;
}> = (props) => {
  const { onClose } = props;
  const { outputs } = useWatch<ValidateTimeSeriesType>();
  const { data: groupsWithDescriptions, isFetching } =
    useDataObjectGroupsWithDescriptionsQuery({
      groupType: DataObjectGroupType.O,
      active: true,
    });
  const groupDescriptionOptions = useGroupDescriptionOptions(
    groupsWithDescriptions,
  );

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              disabled={!outputs || outputs.length === 0}
              label={t('button.validate')}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{t('title')}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <OutputQuickSelect
          options={groupDescriptionOptions}
          isLoading={isFetching}
        />
        <Padding top>
          <TimeSeriesOutputsTable
            groupsWithDescriptionsOptions={groupDescriptionOptions}
            isLoading={isFetching}
          />
        </Padding>
      </Padding>
    </Modal>
  );
};
